import { FC, SyntheticEvent } from 'react';

import { Icon } from '@purpur/library/icon';
import { chevronLeft } from '@purpur/library/icon/assets/chevron-left';
import { chevronRight } from '@purpur/library/icon/assets/chevron-right';
import styled from 'styled-components';

import { getPurpurColor, getPurpurSpacing, purpurMedia } from '@telialt/common-theme/src/purpurSelectors';

import { GALLERY_ITEM_HEIGHT } from './constants';

type NavButtonProps = {
    onClick?: (event: SyntheticEvent<HTMLElement>) => void;
    className?: string;
};

const NavButton = styled.button<NavButtonProps>`
    cursor: pointer;
    width: 2.5rem;
    height: 2.5rem;

    border-radius: 50%;
    line-height: 1.875rem;
    vertical-align: middle;

    background-color: transparent;

    align-items: center;
    border: 1px solid ${getPurpurColor('functionalBlack')};
    color: ${getPurpurColor('functionalBlack')};
    display: flex;
    justify-content: center;
    margin: 0;

    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;

    &:hover {
        color: rgba(0, 0, 0, 0.7);
        border: 1px solid ${getPurpurColor('purple500')};
    }

    &.slick-prev,
    &.slick-next {
        color: ${getPurpurColor('functionalBlack')};
    }

    &.slick-prev:hover,
    &.slick-next:hover {
        color: ${getPurpurColor('purple500')};
    }

    &:before {
        content: none;
    }

    &.slick-disabled {
        cursor: default;
        visibility: hidden;
        opacity: 0;
    }
`;

const NavButtonPrev = styled(NavButton)<NavButtonProps>`
    left: 0.625rem;

    ${purpurMedia('md')`
        left: -1.25rem;
    `};

    ${purpurMedia('lg')`
        left: -1.5625rem;
    `};
`;

const NavButtonNext = styled(NavButton)<NavButtonProps>`
    right: 0.625rem;

    ${purpurMedia('md')`
        right: -1.25rem;
    `};

    ${purpurMedia('lg')`
        right: -1.5625rem;
    `};
`;

type GalleryNavButtonProps = {
    variant: 'prev' | 'next';
} & NavButtonProps;

export const GalleryNavButton: FC<GalleryNavButtonProps> = ({ variant, onClick, className }) => {
    if (variant === 'prev') {
        return (
            <NavButtonPrev onClick={onClick} className={className}>
                <Icon svg={chevronLeft} />
            </NavButtonPrev>
        );
    }

    return (
        <NavButtonNext onClick={onClick} className={className}>
            <Icon svg={chevronRight} />
        </NavButtonNext>
    );
};

export const GalleryWrapper = styled.div`
    position: relative;
    z-index: 0;
`;

export const GalleryItem = styled.div`
    cursor: zoom-in;
    float: left;
    position: relative;
    height: ${GALLERY_ITEM_HEIGHT.MOBILE};
    outline: none;

    ${purpurMedia('md')`
        height: ${GALLERY_ITEM_HEIGHT.DESKTOP};
    `};
`;

export const GalleryItemImg = styled.img<{ $isBlurred?: boolean }>`
    ${({ $isBlurred }) => $isBlurred && purpurMedia('lg')`filter: blur(3px) opacity(0.5);`}
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    max-width: 100%;
    max-height: 100%;
    margin: auto;
`;

export const GalleryThumbnailsInRows = styled.div`
    padding: 0;
    margin-top: ${getPurpurSpacing('400')};
    margin-bottom: ${getPurpurSpacing('400')};

    & .slick-slide {
        display: grid;
        justify-content: center;
    }

    .slick-slide div:focus {
        outline: revert !important;
    }

    & .slick-slide > div:not(:last-of-type) {
        display: flex;
        justify-content: flex-start;
        gap: 1rem;
        margin-bottom: ${getPurpurSpacing('200')};
    }

    & .slick-slide > div:nth-last-of-type(2) {
        margin-bottom: 0;
    }

    & .slick-list {
        margin: 0 auto;
        max-width: 660px;
    }
`;

type GalleryThumbnailProps = {
    $isActive: boolean;
};

type ShowMoreThumbnailsButtonProps = {
    $isShowMore: boolean;
};

export const AccessibilityDiv = styled.div`
    height: 3rem;
    line-height: 3rem;
    width: 3rem !important;

    ${purpurMedia('lg')`
        height: 4rem;
        width: 4rem !important;
        line-height: 4rem;
    `};
`;

export const GalleryThumbnail = styled.div<GalleryThumbnailProps>`
    cursor: pointer;
    float: left;
    font-size: 0;
    height: 3rem;
    line-height: 3rem;
    opacity: ${({ $isActive }) => ($isActive ? '1' : '0.25')};
    outline-offset: -1px;
    transition: opacity 0.15s ease-in-out;
    width: 3rem !important;

    &.active {
        opacity: 1;
    }

    ${purpurMedia('lg')`
        height: 4rem;
        width: 4rem !important;
        line-height: 4rem;

        &:hover {
            opacity: 1;
        }
    `};
`;

export const ButtonAccessibilityDiv = styled.div<ShowMoreThumbnailsButtonProps>`
    display: ${({ $isShowMore }) => ($isShowMore ? 'block' : 'none !important')};
    height: 3rem;
    width: 3rem !important;

    ${purpurMedia('md')`
        display: none !important;
    `};
`;

export const ShowMoreThumbnailsButton = styled.button<ShowMoreThumbnailsButtonProps>`
    display: ${({ $isShowMore }) => ($isShowMore ? 'block' : 'none !important')};
    background: none;
    border: 1px solid ${getPurpurColor('gray200')};
    border-radius: 0.25rem;
    height: 3rem;
    width: 3rem !important;
    font-size: 1.5rem;
    font-weight: bold;
    color: ${getPurpurColor('purple900')};
    padding: 0;

    ${purpurMedia('md')`
        display: none !important;
    `};
`;

export const GalleryThumbnailImg = styled.img`
    display: inline-block;
    margin: 0 auto;
    max-height: 100%;
    max-width: 100%;
`;
